import { LogLevel, IPublicClientApplication, PublicClientApplication, BrowserCacheLocation } from '@azure/msal-browser';
import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
} from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';
import { environment } from '../../environments/environment';

// Installed PWAs lose the popup window when iOS/Android hand off to the system browser,
// so we fall back to redirect there. Everywhere else, popup keeps the user on the page
// and avoids the broker's "close this tab" handoff screen.
export function isPwaStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  const displayModeStandalone = window.matchMedia?.('(display-mode: standalone)').matches === true;
  return iosStandalone || displayModeStandalone;
}

function preferredInteractionType(): InteractionType.Popup | InteractionType.Redirect {
  return isPwaStandalone() ? InteractionType.Redirect : InteractionType.Popup;
}

export function msalInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: environment.msal.authority,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message) => {
          if (!environment.production && level <= LogLevel.Warning) {
            // eslint-disable-next-line no-console
            console.log(`[MSAL] ${message}`);
          }
        },
        logLevel: environment.production ? LogLevel.Warning : LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: preferredInteractionType(),
    authRequest: {
      // Include Graph scopes so a guard-triggered redirect login goes through
      // the same consent flow as the AuthService's popup. Keeps PWA / redirect
      // users from ending up with a token cache that lacks Mail.Read.
      scopes: [...environment.msal.apiScopes, ...environment.msal.graphScopes],
    },
  };
}

export function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const apiBase = environment.apiBaseUrl.replace(/\/+$/, '');
  const map = new Map<string, Array<string>>();
  // Outbound calls to our REST endpoints get the API access token.
  // Narrowed to /api/* (was /*): SignalR hub URLs at /hubs/* must NOT be wrapped
  // by this interceptor. The hub uses accessTokenFactory + ?access_token=...
  // query string, and adding a Bearer header alongside causes the JWT bearer
  // handler to see two tokens and reject the upgrade.
  map.set(`${apiBase}/api/*`, [...environment.msal.apiScopes]);
  return {
    interactionType: preferredInteractionType(),
    protectedResourceMap: map,
  };
}
